/**
 * FormateurSession.jsx
 * Page d'accès formateur via QR Code + PIN
 * Après vérification du PIN, connecte le formateur avec un JWT limité
 * et le redirige vers SessionDetail (3 onglets : présences, questionnaires, résultats)
 * Route : /f/:token
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function FormateurSession() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginWithToken, isFormateur, formateurSessionId } = useAuth();

  const [pin, setPin] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoLoading, setInfoLoading] = useState(true);

  // Charger les infos de la session depuis le token (sans PIN, juste les infos publiques)
  useEffect(() => {
    if (isFormateur && formateurSessionId) {
      navigate(`/sessions/${formateurSessionId}`, { replace: true });
      return;
    }
    fetch(`${API_BASE}/formateur-session/${token}/info`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSessionInfo(d); })
      .catch(() => {})
      .finally(() => setInfoLoading(false));
  // eslint-disable-next-line
  }, [token]);

  async function handleVerify() {
    if (pin.length !== 6) { setError('Le PIN doit comporter 6 chiffres'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/formateur-session/${token}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Code incorrect'); return; }
      // Connexion via JWT formateur
      loginWithToken(data.jwt);
      // Redirection vers la session dans l'app principale
      navigate(`/sessions/${data.session_id}`, { replace: true });
    } catch (e) { setError('Erreur de connexion'); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', padding: '1rem' }}>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '2.5rem 2rem', width: '380px', maxWidth: '95vw', textAlign: 'center' }}>
        {/* Logo */}
        <div style={{ marginBottom: '1.2rem' }}>
          <svg viewBox="0 0 230 76" width="130" height="43" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="119" cy="46" rx="107" ry="32" fill="#828282"/>
            <ellipse cx="114" cy="38" rx="107" ry="32" fill="#F2A030"/>
            <text x="114" y="39" textAnchor="middle" dominantBaseline="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="30" fill="#ffffff">CEJ Concept</text>
          </svg>
        </div>

        <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>🔐</div>
        <h2 style={{ color: '#f1f5f9', marginBottom: '0.3rem', fontSize: '1.25rem' }}>Accès Formateur</h2>

        {/* Infos session */}
        {sessionInfo && (
          <div style={{ background: 'rgba(232,119,34,0.1)', border: '1px solid rgba(232,119,34,0.3)', borderRadius: '8px', padding: '0.7rem 1rem', marginBottom: '1.2rem', textAlign: 'left' }}>
            <div style={{ color: '#E87722', fontWeight: 700, fontSize: '0.9rem' }}>{sessionInfo.intitule_formation}</div>
            <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '0.2rem' }}>
              {formatDate(sessionInfo.date_debut)} → {formatDate(sessionInfo.date_fin)}
            </div>
            {sessionInfo.nom_formateur && (
              <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>👤 {sessionInfo.nom_formateur}</div>
            )}
          </div>
        )}

        <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
          Saisissez le code PIN à 6 chiffres reçu par email.
        </p>

        <input
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="• • • • • •"
          value={pin}
          onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={e => e.key === 'Enter' && handleVerify()}
          style={{
            width: '100%', padding: '0.9rem 1rem', borderRadius: '10px',
            border: `2px solid ${error ? '#ef4444' : '#475569'}`,
            background: '#0f172a', color: '#f1f5f9',
            fontSize: '1.8rem', textAlign: 'center', letterSpacing: '0.4em',
            boxSizing: 'border-box', marginBottom: '1rem', outline: 'none',
          }}
          autoFocus
        />

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '0.6rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <button
          onClick={handleVerify}
          disabled={loading || pin.length !== 6}
          style={{
            width: '100%', padding: '0.9rem', borderRadius: '10px', border: 'none',
            background: pin.length === 6 ? '#E87722' : '#334155',
            color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: pin.length === 6 ? 'pointer' : 'default',
            opacity: loading ? 0.7 : 1, transition: 'background 0.2s',
          }}>
          {loading ? '⏳ Connexion…' : '▶ Accéder à ma session'}
        </button>
      </div>
    </div>
  );
}
